import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Divider,
  InputAdornment,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { SectionLabel, PROVIDER_LABEL } from "./_wizardUi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGitlabSquare,
  faBitbucket,
  faGithub,
} from "@fortawesome/free-brands-svg-icons";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import GitIcon from "@mui/icons-material/Folder";
import { useDispatch } from "react-redux";
import { useLoader } from "../../../../context/LoaderContext";
import { AppDispatch } from "../../../../store";

import {
  fetchBranches,
  fetchTags,
  registryProcessFramework,
} from "../../../../features/slices/Registry";
import { useNotification } from "../../../../context/NotificationContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchSystem,
  postSystem,
} from "../../../../features/slices/SystemRegistry";

interface GitFrameProps {
  suiteId: number | string;
  gitFrameData: (data: boolean) => void;
}
const GitFrame = ({ suiteId, gitFrameData }: GitFrameProps) => {
  const [gitUrl, setGitUrl] = useState("");
  const [authType, setAuthType] = useState("token");
  const [username, setUsername] = useState("");
  const [token, setToken] = useState("");
  const [sshFile, setSshFile] = useState(null);
  const [sshPassphrase, setSshPassphrase] = useState("");
  const [hasPassphrase, setHasPassphrase] = useState(false);
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [tags, setTags] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [iconType, setIconType] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [saveCredentials, setSaveCredentials] = useState(false);
  const [dropdownType, setDropdownType] = useState<string>("branches");
  const [fetchType, setFetchType] = useState("branches");
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useNotification();

  const validateUrl = (url: string) => {
    const githubPattern = /^(https:\/\/|git@)github\.com[:\/].*\.git$/;
    const gitlabPattern =
      /^(https:\/\/(?:[^@]+@)?gitlab\.com\/[\w\-]+\/[\w\-]+\.git|git@gitlab\.com:[\w\-]+\/[\w\-]+\.git)$/;
    const bitbucketPattern =
      /^(https:\/\/(?:[^@]+@)?bitbucket\.org\/[\w\-]+\/[\w\-]+\.git|git@bitbucket\.org:[\w\-]+\/[\w\-]+\.git)$/;
    const gitPattern = /^(https:\/\/|git@).*\.git$/;

    if (url.startsWith("git@")) {
      setAuthType("ssh_key");
      setIsValidUrl(true);
      setIconType("git");
      notify("SSH repository detected.", "success");
    } else if (githubPattern.test(url)) {
      setAuthType("token");
      setIsValidUrl(true);
      setIconType("github");
      notify("GitHub repository detected.", "success");
    } else if (gitlabPattern.test(url)) {
      setAuthType("token");
      setIsValidUrl(true);
      setIconType("gitlab");
      notify("GitLab repository detected.", "success");
    } else if (bitbucketPattern.test(url)) {
      setAuthType("token");
      setIsValidUrl(true);
      setIconType("bitbucket");
      notify("Bitbucket repository detected.", "success");
    } else if (gitPattern.test(url)) {
      setAuthType("token");
      setIsValidUrl(true);
      setIconType("git");
      notify("Git repository detected.", "success");
    } else {
      setIsValidUrl(false);
      setIconType(null);
      notify("Invalid Git URL. Please enter a valid repository URL.", "error");
    }
  };

  useEffect(() => {
    switch (authType) {
      case "token":
        setIsFormValid(
          isValidUrl && username.trim() !== "" && token.trim() !== ""
        );
        break;
      case "ssh_key":
        setIsFormValid(isValidUrl && sshFile !== null);
        break;
      default:
        setIsFormValid(false);
        break;
    }
  }, [authType, gitUrl, username, token, sshFile, isValidUrl]);

  const handleFileUpload = (event: any) => {
    setSshFile(event.target.files[0]);
  };
  const handleSshSubmit = async () => {
    if (!sshFile) {
      notify("Please upload an SSH key file.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("repo", gitUrl);
    formData.append("title", gitUrl.split("/").pop().replace(".git", ""));
    formData.append("reg_type", "ssh_key");
    formData.append("file", sshFile);
    formData.append("entity_type", "framework");
    const regValue = JSON.stringify({ token: sshPassphrase });
    if (hasPassphrase) {
      formData.append("reg_value", regValue);
    }

    try {
      const response = await dispatch(postSystem(formData)).unwrap();
      if (response.reg_value) {
        notify(
          "SSH key uploaded successfully and repository registered.",
          "success"
        );
        setIsFormValid(true);
      } else {
        throw new Error("Failed to register SSH key.");
      }
    } catch (error) {
      notify(
        "Failed to register repository with SSH key. Please try again.",
        "error"
      );
    }
  };

  useEffect(() => {
    if (gitUrl && authType) loadCredentials(authType);
  }, [authType, gitUrl]);

  const loadCredentials = async (type: string) => {
    const params = {
      reg_type: type,
      repo: gitUrl,
      mode: "first",
      entity_type: "framework",
    };
    const query = new URLSearchParams(params).toString();

    try {
      const response = await dispatch(fetchSystem(query)).unwrap();
      const cred = response;
      setSaveCredentials(!!cred);
      const regValue = cred.reg_value || {};
      setUsername(regValue.username || "");
      setToken(regValue.token || "");
      setGitUrl(cred.repo);
      await fetchBranchesHandler();
      setIsValidUrl(true);
      notify("Credentials loaded successfully", "success");
    } catch (error) {
      setSaveCredentials(false);
      notify("No credentials found for this repository.", "warning");
      console.error("Failed to load credentials:", error);
    }
  };

  const handleSaveCredentials = async () => {
    if (saveCredentials) {
      const formData = new FormData();
      formData.append("repo", gitUrl);
      formData.append("title", gitUrl.split("/").pop().replace(".git", ""));
      formData.append("reg_type", authType);
      formData.append("entity_type", "framework");

      const regValue = JSON.stringify({
        username,
        token:
          authType === "token"
            ? token
            : authType === "ssh_key"
            ? sshPassphrase
            : "",
      });

      formData.append("reg_value", regValue);

      if (authType === "ssh_key" && sshFile) {
        formData.append("file", sshFile);
      }

      try {
        await dispatch(postSystem(formData)).unwrap();
        notify("Credentials saved successfully", "success");
      } catch (error) {
        notify("Failed to save credentials", "error");
      }
    }
  };

  const fetchBranchesHandler = async () => {
    if (!isValidUrl || !isFormValid) {
      notify(
        "Please ensure all required fields are filled out correctly.",
        "error"
      );
      return;
    }
    setLoadingBranches(true);
    setDropdownType("branches");

    const requestData = {
      repo_url: gitUrl,
      username: username,
      password: authType === "token" ? token : "",
      token: authType === "token" ? token : "",
      reg_type: "git",
      suite_id: `${suiteId}`,
    };

    try {
      const response = await dispatch(fetchBranches(requestData)).unwrap();
      setBranches(response || []);
      setLoadingBranches(false);
      if (response.length > 0) {
        notify("Branches fetched successfully!", "success");
      } else {
        notify("No branches found in the repository.", "warning");
        setDropdownType(null);
      }
    } catch (error) {
      setLoadingBranches(false);
      notify(
        "Failed to fetch branches. Please check your credentials and try again.",
        "error"
      );
      setDropdownType(null);
    }
  };

  const fetchTagsHandler = async () => {
    if (!isValidUrl || !isFormValid) {
      notify(
        "Please ensure all required fields are filled out correctly.",
        "error"
      );
      return;
    }
    setLoadingTags(true);
    setDropdownType("tags");

    const requestData = {
      repo_url: gitUrl,
      username: authType === "token" ? username : "",
      password: authType === "token" ? token : "",
      token: authType === "token" ? token : "",
      reg_type: "git",
      suite_id: `${suiteId}`,
    };

    try {
      const response = await dispatch(fetchTags(requestData)).unwrap();
      setTags(response || []);
      setLoadingTags(false);
      if (response.length > 0) {
        notify("Tags fetched successfully!", "success");
      } else {
        notify("No tags found in the repository.", "warning");
        setDropdownType(null);
      }
    } catch (error) {
      setLoadingTags(false);
      notify(
        "Failed to fetch tags. Please check your credentials and try again.",
        "error"
      );
      setDropdownType(null);
    }
  };

  const handleSubmit = async () => {
    showLoader();
    const requestData = {
      repo_url: gitUrl,
      username: authType === "token" ? username : "",
      password: authType === "token" ? token : "",
      reg_type: "git",
      branch: branch,
      token: "",
      suite_id: `${suiteId}`,
    };
    try {
      const response = await dispatch(
        registryProcessFramework(requestData)
      ).unwrap();
      if (typeof response === "string") {
        notify("Framework applied successfully!", "success");
        await handleSaveCredentials();
        gitFrameData(true);
      } else if (response && response.error) {
        notify(
          "Failed to apply framework: " +
            response.error +
            (response.details ? "\n" + response.details : ""),
          "error"
        );
      } else {
        notify("Failed to apply framework. Please try again.", "error");
      }
    } catch (error) {
      if (error && error.error) {
        notify(
          "Framework error: " +
            error.error +
            (error.details ? "\n" + error.details : ""),
          "error"
        );
      } else {
        notify(
          "Unexpected error while applying framework: " +
            (error?.toString() ?? ""),
          "error"
        );
      }
      console.error(error);
    } finally {
      hideLoader();
    }
  };

  const getIcon = () => {
    switch (iconType) {
      case "github":
        return <FontAwesomeIcon icon={faGithub} size="2x" />;
      case "gitlab":
        return (
          <FontAwesomeIcon icon={faGitlabSquare} color="orange" size="2x" />
        );
      case "bitbucket":
        return <FontAwesomeIcon icon={faBitbucket} color="blue" size="2x" />;
      case "git":
        return <GitIcon color="action" />;
      default:
        return null;
    }
  };

  const fetching = loadingBranches || loadingTags;

  return (
    <Stack spacing={3}>
      {/* ---- Repository ---- */}
      <Box>
        <SectionLabel>Repository</SectionLabel>
        <TextField
          autoComplete="off"
          label="Git framework repository URL"
          placeholder="https://github.com/org/repo.git"
          value={gitUrl}
          onChange={(e) => {
            setGitUrl(e.target.value);
            validateUrl(e.target.value);
          }}
          fullWidth
          error={Boolean(gitUrl) && !isValidUrl}
          helperText={
            !gitUrl
              ? "Paste an HTTPS or SSH .git URL — the provider is detected automatically."
              : isValidUrl
              ? `Detected: ${PROVIDER_LABEL[iconType ?? "git"] ?? "Git"} repository`
              : "That doesn't look like a valid .git URL."
          }
          InputProps={{
            endAdornment: gitUrl ? (
              <InputAdornment position="end">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {getIcon()}
                  {isValidUrl ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </Stack>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {/* ---- Authentication ---- */}
      <Box
        sx={{
          opacity: isValidUrl ? 1 : 0.5,
          pointerEvents: isValidUrl ? "auto" : "none",
          transition: "opacity 150ms",
        }}
      >
        <SectionLabel>Authentication</SectionLabel>
        <Stack spacing={2}>
          <FormControl fullWidth disabled={!isValidUrl}>
            <InputLabel>Authentication type</InputLabel>
            <Select
              value={authType}
              label="Authentication type"
              onChange={(e) => setAuthType(e.target.value)}
            >
              <MenuItem value="token">Token</MenuItem>
              {/* <MenuItem value="ssh_key">SSH Key</MenuItem> */}
            </Select>
          </FormControl>

          {authType === "token" && (
            <>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  autoComplete="off"
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  fullWidth
                />
                <TextField
                  autoComplete="off"
                  label="Access token"
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  fullWidth
                />
              </Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={saveCredentials}
                    onChange={(e) => setSaveCredentials(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Remember these credentials for this repository
                  </Typography>
                }
              />
            </>
          )}

          {authType === "ssh_key" && (
            <Stack spacing={1.5} alignItems="flex-start">
              <Button variant="outlined" component="label">
                Upload SSH key file
                <input type="file" hidden onChange={handleFileUpload} />
              </Button>
              {sshFile && (
                <Typography variant="body2" color="text.secondary">
                  {sshFile.name} ({(sshFile.size / 1024).toFixed(2)} KB)
                </Typography>
              )}
              {sshFile && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={hasPassphrase}
                      onChange={(e) => setHasPassphrase(e.target.checked)}
                    />
                  }
                  label="This SSH key requires a passphrase"
                />
              )}
              {hasPassphrase && (
                <TextField
                  label="SSH key passphrase"
                  type="password"
                  value={sshPassphrase}
                  onChange={(e) => setSshPassphrase(e.target.value)}
                  fullWidth
                  helperText="Leave empty if your SSH key has no passphrase."
                />
              )}
              <Button
                variant="contained"
                onClick={handleSshSubmit}
                disabled={!sshFile}
              >
                Register SSH key
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* ---- Branch / tag ---- */}
      <Box
        sx={{
          opacity: isFormValid ? 1 : 0.5,
          pointerEvents: isFormValid ? "auto" : "none",
          transition: "opacity 150ms",
        }}
      >
        <SectionLabel>Branch / tag</SectionLabel>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <FormControl sx={{ minWidth: 150 }} disabled={!isValidUrl}>
            <InputLabel>Fetch</InputLabel>
            <Select
              value={fetchType}
              label="Fetch"
              onChange={(e) => setFetchType(e.target.value)}
            >
              <MenuItem value="branches">Branches</MenuItem>
              <MenuItem value="tags">Tags</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={
              fetchType === "branches" ? fetchBranchesHandler : fetchTagsHandler
            }
            disabled={!isFormValid || fetching}
            startIcon={fetching ? <CircularProgress size={18} /> : null}
            sx={{ whiteSpace: "nowrap" }}
          >
            {fetchType === "branches" ? "Fetch branches" : "Fetch tags"}
          </Button>

          {dropdownType === "branches" && branches.length > 0 && (
            <FormControl sx={{ flex: 1, minWidth: 200 }}>
              <InputLabel>Branch</InputLabel>
              <Select
                value={branch}
                label="Branch"
                onChange={(e) => setBranch(e.target.value)}
              >
                {branches.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {dropdownType === "tags" && tags.length > 0 && (
            <FormControl sx={{ flex: 1, minWidth: 200 }}>
              <InputLabel>Tag</InputLabel>
              <Select
                value={branch}
                label="Tag"
                onChange={(e) => setBranch(e.target.value)}
              >
                {tags.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Box>

      <Divider />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!branch}
        >
          Apply framework
        </Button>
      </Box>
    </Stack>
  );
};

export default GitFrame;
